import { ref, computed, nextTick, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import {
  UpdateTransPickupRequestDocument,
  type UpdateTransPickupRequestMutation,
  type UpdateTransPickupRequestMutationVariables,
  type TransPickupRequestUpdateInput,
  type OperationInput,
} from "@/generated/graphql";
import type { FetchResult } from "@apollo/client/core";

interface UseDeliveryMoveEditFormParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  editPage: Ref<boolean>;
  confirmPage: Ref<boolean>;
  processFlag: Ref<number>;
  overlay: Ref<boolean>;
  editDeliveryRequest: Ref<TransPickupRequestUpdateInput | null>;
  shopSelectValues_from: Ref<{ shop_code: string; name: string }>;
  shopSelectValues_to: Ref<{ shop_code: string; name: string }>;
  dummy_payment_amount: Ref<string>;
  deliveryNotesBefore: Ref<string>;
  messageDeliveryNotes: Ref<string>;
  deliveryId_0_Flg: Ref<boolean>;
  irregularDeliveryStartTime: Ref<string>;
  irregularDeliveryEndTime: Ref<string>;
  overDeliveryEndTime: Ref<boolean>;
  acceptableEndTime: Ref<string>;
  doubleDeliOriconCount: Ref<number | null>;
  charterDeliOriconCount: Ref<number | null>;
  isDoubleDeli: Ref<boolean>;
  isCharterDeli: Ref<boolean>;
  pre_double_deli_oricon_flg: Ref<number | null>;
  pre_charter_deli_oricon_flg: Ref<number | null>;
  wordList: Record<string, string>;
  onClickSearch: (initail_mode?: boolean) => Promise<void>;
  openListPage: () => void;
}

/**
 * 編集フォームの入力状態と値同期処理を提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useDeliveryMoveEditForm = ({
  errdialog,
  errMessage,
  editPage,
  confirmPage,
  processFlag,
  overlay,
  editDeliveryRequest,
  shopSelectValues_from,
  shopSelectValues_to,
  dummy_payment_amount,
  deliveryNotesBefore,
  messageDeliveryNotes,
  deliveryId_0_Flg,
  irregularDeliveryStartTime,
  irregularDeliveryEndTime,
  overDeliveryEndTime,
  acceptableEndTime,
  doubleDeliOriconCount,
  charterDeliOriconCount,
  isDoubleDeli,
  isCharterDeli,
  pre_double_deli_oricon_flg,
  pre_charter_deli_oricon_flg,
  wordList,
  onClickSearch,
  openListPage,
}: UseDeliveryMoveEditFormParams) => {
  /**
   * 額カンマ処理
   */
  const commmaMoney = async (value: string): Promise<void> => {
    if (dummy_payment_amount.value !== "") {
      dummy_payment_amount.value = String(value).replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1,"
      );
    }
  };

  /**
   * カンマ削除処理
   */
  const removeComma = async (value: string): Promise<void> => {
    if (dummy_payment_amount.value !== "") {
      dummy_payment_amount.value = String(value)
        .replace(/,/g, "")
        .replace(/¥/g, "");
    }
  };

  /**
   * 金額フォーカスON
   */
  const onFocus = async (value: string): Promise<void> => {
    await removeComma(value);
  };

  /**
   * 金額フォーカスアウト
   */
  const onBlur = async (value: string): Promise<void> => {
    // カンマ削除
    await removeComma(value);

    // 数値チェック
    if (value) {
      const regex = new RegExp("^[0-9]+$");
      if (!regex.test(value)) {
        errdialog.value = true;
        errMessage.value = "数値およびカンマのみ入力してください";
        return;
      }
    }

    // カンマ付与
    await commmaMoney(value);
  };

  /**
   * 配達依頼編集 登録(確認) @Click
   */
  const onClickUpdate = async (): Promise<void> => {
    //カンマ削除
    await removeComma(dummy_payment_amount.value);
    //数値チェック
    if (dummy_payment_amount.value) {
      const regex = new RegExp("^[0-9]+$");
      if (!regex.test(dummy_payment_amount.value)) {
        errdialog.value = true;
        errMessage.value = "支払金額は数値およびカンマのみ入力してください";
        return;
      }
    }
    //カンマ付与
    await commmaMoney(dummy_payment_amount.value);

    //店舗コードを設定
    editDeliveryRequest.value!.delivery_shop_code = shopSelectValues_to.value.shop_code;
    editDeliveryRequest.value!.ship_from_shop_code = shopSelectValues_from.value.shop_code;
    // 編集ページ表示フラグ
    editPage.value = false;

    // 確認ページ表示フラグ
    confirmPage.value = true;

    // 配達備考のオリジナルを保持し、ダブルクォーテーションを削除
    if (editDeliveryRequest.value?.delivery_notes?.includes('"')) {
      deliveryNotesBefore.value = editDeliveryRequest.value.delivery_notes;
      editDeliveryRequest.value.delivery_notes =
        editDeliveryRequest.value.delivery_notes.replace(/"/g, "");
      messageDeliveryNotes.value =
        "(※配達備考のダブルクォーテーションは削除されています)";
    }

    // ２件分配達もしくはチャーター便であるかを判定
    isDoubleDeli.value = false;
    isCharterDeli.value = false;
    if (
      doubleDeliOriconCount.value !== null &&
      editDeliveryRequest.value!.unit >= doubleDeliOriconCount.value &&
      editDeliveryRequest.value!.unit < charterDeliOriconCount.value!
    ) {
      isDoubleDeli.value = true;
    } else if (
      charterDeliOriconCount.value !== null &&
      editDeliveryRequest.value!.unit >= charterDeliOriconCount.value!
    ) {
      isCharterDeli.value = true;
    }
  };

  /**
   * 配達依頼更新確認キャンセル @Click
   */
  const onClickConfirmUpdateCancel = async (): Promise<void> => {
    if (processFlag.value > 0) {
      // 編集ページ表示フラグ
      editPage.value = true;
    } else {
      editPage.value = false;
    }

    // 確認ページ表示フラグ
    confirmPage.value = false;
  };

  /**
   * 配達依頼更新確認OK @Click
   */
  const onClickConfirmUpdateOk = async (): Promise<void> => {
    overlay.value = true;
    //graphql接続
    const apolloClient = await generateApolloClient();

    if (editDeliveryRequest.value === null) {
      return;
    }
    //支払金額のカンマを削除して上書き
    const removed = dummy_payment_amount.value.replace(/,/g, "");
    editDeliveryRequest.value.payment_amount = parseInt(removed, 10);

    if (deliveryId_0_Flg.value) {
      //都度便の場合、配達時間セット
      editDeliveryRequest.value.irregular_delivery_start_time =
        irregularDeliveryStartTime.value + ":00";
      editDeliveryRequest.value.irregular_delivery_end_time =
        irregularDeliveryEndTime.value + ":00";
    } else {
      editDeliveryRequest.value.irregular_delivery_start_time = "";
      editDeliveryRequest.value.irregular_delivery_end_time = "";
    }

    editDeliveryRequest.value.pre_double_deli_oricon_flg =
      pre_double_deli_oricon_flg.value;
    editDeliveryRequest.value.pre_charter_deli_oricon_flg =
      pre_charter_deli_oricon_flg.value;
    if (isDoubleDeli.value) {
      editDeliveryRequest.value.double_deli_oricon_flg = 1;
    } else {
      editDeliveryRequest.value.double_deli_oricon_flg = 0;
    }
    if (isCharterDeli.value) {
      editDeliveryRequest.value.charter_deli_oricon_flg = 1;
    } else {
      editDeliveryRequest.value.charter_deli_oricon_flg = 0;
    }

    // 配達依頼更新 input
    const input: TransPickupRequestUpdateInput = {
      area_code: editDeliveryRequest.value.area_code,
      chain_code: editDeliveryRequest.value.chain_code,
      customer_code: editDeliveryRequest.value.customer_code,
      delivery_id: editDeliveryRequest.value.delivery_id,
      delivery_notes: editDeliveryRequest.value.delivery_notes,
      delivery_shop_code: editDeliveryRequest.value.delivery_shop_code,
      payment_amount: editDeliveryRequest.value.payment_amount,
      payment_method: editDeliveryRequest.value.payment_method,
      receipt_no: editDeliveryRequest.value.receipt_no,
      reception_no: editDeliveryRequest.value.reception_no,
      register_no: editDeliveryRequest.value.register_no,
      required_delivery_date: editDeliveryRequest.value.required_delivery_date,
      ship_from_shop_code: editDeliveryRequest.value.ship_from_shop_code,
      unit: editDeliveryRequest.value.unit,
      irregular_delivery_start_time:
        editDeliveryRequest.value.irregular_delivery_start_time,
      irregular_delivery_end_time:
        editDeliveryRequest.value.irregular_delivery_end_time,
      pre_double_deli_oricon_flg:
        editDeliveryRequest.value.pre_double_deli_oricon_flg,
      pre_charter_deli_oricon_flg:
        editDeliveryRequest.value.pre_charter_deli_oricon_flg,
      double_deli_oricon_flg: editDeliveryRequest.value.double_deli_oricon_flg,
      charter_deli_oricon_flg: editDeliveryRequest.value.charter_deli_oricon_flg,
    };

    // 配達依頼更新 logInput
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 配達依頼更新
    let result: FetchResult<UpdateTransPickupRequestMutation>;
    try {
      result = await apolloClient.mutate<
        UpdateTransPickupRequestMutation,
        UpdateTransPickupRequestMutationVariables
      >({
        mutation: UpdateTransPickupRequestDocument,
        variables: {
          input,
          logInput,
        },
      });
    } catch (err) {
      console.error("err=", err);
      errdialog.value = true;
      errMessage.value = "在庫移動・回収の更新に失敗しました。";
      overlay.value = false;
      return;
    }

    if (
      !result.data ||
      !result.data.updateTransPickupRequest
    ) {
      // 配達依頼更新 エラー
      console.error("result.data.updateTransPickupRequest is undefined or null.");
      errdialog.value = true;
      errMessage.value = "在庫移動・回収の更新に失敗しました。";
      overlay.value = false;
      return;
    }

    //受付可能終了時間超過チェック
    const nowDate = new Date();
    const strNowDate =
      `${nowDate.getFullYear()}-` +
      `${String(nowDate.getMonth() + 1).padStart(2, "0")}-` +
      `${String(nowDate.getDate()).padStart(2, "0")}`;
    const acceptableEndTimeAry = acceptableEndTime.value.split(":");
    if (
      deliveryId_0_Flg.value &&
      editDeliveryRequest.value.required_delivery_date === strNowDate &&
      (Number(acceptableEndTimeAry[0]) < nowDate.getHours() ||
        (Number(acceptableEndTimeAry[0]) === nowDate.getHours() &&
          Number(acceptableEndTimeAry[1]) < nowDate.getMinutes()))
    ) {
      overDeliveryEndTime.value = true;
      overlay.value = false;
      errdialog.value = true;
      errMessage.value = wordList["lbl_delivery_time_chk"];
    }

    // 編集対象 配達依頼
    editDeliveryRequest.value = null;
    // 一覧表示を表示する
    openListPage();

    await nextTick(async () => {
      // 絞り込み検索（ページを維持するためinitail_mode=trueで呼び出し）
      await onClickSearch(true);
    });
  };

  /**
   * ２件分配達メッセージ取得＆置換
   */
  const doubleDeliCheckLabel = computed((): string => {
    return wordList.lbl_double_entry_chk.replace(
      "{{n}}",
      (doubleDeliOriconCount.value ?? 0).toString()
    );
  });

  /**
   * チャーター便メッセージ取得＆置換
   */
  const charterDeliChkLabel = computed((): string => {
    return wordList.lbl_charter_chk.replace(
      "{{n}}",
      (charterDeliOriconCount.value ?? 0).toString()
    );
  });

  return {
    commmaMoney,
    removeComma,
    onFocus,
    onBlur,
    onClickUpdate,
    onClickConfirmUpdateCancel,
    onClickConfirmUpdateOk,
    doubleDeliCheckLabel,
    charterDeliChkLabel,
  };
};
